import { useState, useEffect } from 'react';
import { X, FolderOpen, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_PHOTOS = 15;

function ProjectForm({ project, selectedPhotos, onClose, onSuccess, onSelectPhotos, onPhotosChange }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    date: '',
    classification: ''
  });
  const [classifications, setClassifications] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchClassifications();
    if (project) {
      setFormData({
        title: project.title,
        description: project.description,
        date: project.date,
        classification: project.classification
      });
    }
  }, [project]);

  const fetchClassifications = async () => {
    try {
      const response = await axios.get(`${API}/classifications`);
      setClassifications(response.data.classifications);
    } catch (error) {
      console.error('Error fetching classifications:', error);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const removePhoto = async (photo, index) => {
    const isPersisted = !!photo.file_key;

    if (isPersisted && project?.project_id) {
      if (selectedPhotos.length <= 1) {
        toast.error('No puedes eliminar la última foto. Elimina el proyecto completo.');
        return;
      }
      if (!window.confirm(`¿Eliminar la foto "${photo.name || 'sin nombre'}" definitivamente?`)) return;
      const token = localStorage.getItem('session_token');
      const photoKey = photo.file_key || photo.id;
      try {
        await axios.delete(`${API}/projects/${project.project_id}/photos/${photoKey}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Foto eliminada');
        onPhotosChange(selectedPhotos.filter((_, i) => i !== index));
      } catch (error) {
        console.error('Error deleting photo:', error);
        toast.error(error.response?.data?.detail || 'Error al eliminar foto');
      }
    } else {
      onPhotosChange(selectedPhotos.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedPhotos.length === 0) {
      toast.error('Selecciona al menos una foto');
      return;
    }
    if (selectedPhotos.length > MAX_PHOTOS) {
      toast.error(`Máximo ${MAX_PHOTOS} fotos por proyecto`);
      return;
    }

    setIsSubmitting(true);
    const token = localStorage.getItem('session_token');

    const newPhotosCount = selectedPhotos.filter(p => !p.file_key).length;

    try {
      const payload = {
        ...formData,
        photos: selectedPhotos
      };

      if (project) {
        await axios.put(
          `${API}/projects/${project.project_id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/projects`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      if (newPhotosCount > 0) {
        toast.success(
          `Proyecto guardado. Procesando ${newPhotosCount} foto(s) en segundo plano…`,
          { duration: 5000 }
        );
      } else {
        toast.success(project ? 'Proyecto actualizado' : 'Proyecto creado');
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving project:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar proyecto');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6">
      <div className="bg-neutral-900 border border-neutral-800 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-neutral-900 border-b border-neutral-800 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl text-white font-medium">
            {project ? 'Editar Proyecto' : 'Nuevo Proyecto'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-white disabled:opacity-30" data-testid="close-form-btn" disabled={isSubmitting}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label htmlFor="title" className="block text-sm text-neutral-400 mb-2">Título *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 text-white focus:border-white focus:outline-none"
              data-testid="project-title-input"
            />
          </div>

          <div>
            <label htmlFor="date" className="block text-sm text-neutral-400 mb-2">Fecha *</label>
            <input
              type="text"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              placeholder="Ej: Enero 2024"
              required
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 text-white focus:border-white focus:outline-none"
              data-testid="project-date-input"
            />
          </div>

          <div>
            <label htmlFor="classification" className="block text-sm text-neutral-400 mb-2">Clasificación *</label>
            <input
              type="text"
              id="classification"
              name="classification"
              value={formData.classification}
              onChange={handleChange}
              list="classifications"
              placeholder="Ej: Documental, Retrato, etc."
              required
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 text-white focus:border-white focus:outline-none"
              data-testid="project-classification-input"
            />
            <datalist id="classifications">
              {classifications.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
            <p className="text-xs text-neutral-500 mt-1">
              Comienza a escribir para autocompletar con clasificaciones existentes
            </p>
          </div>

          <div>
            <label htmlFor="description" className="block text-sm text-neutral-400 mb-2">Descripción *</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="4"
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 text-white focus:border-white focus:outline-none resize-none"
              data-testid="project-description-textarea"
            ></textarea>
          </div>

          {/* Photos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm text-neutral-400">
                Fotos * <span className="text-neutral-600">(máx. {MAX_PHOTOS}, 10MB c/u)</span>
              </label>
              <span className={`text-xs ${selectedPhotos.length >= MAX_PHOTOS ? 'text-amber-400' : 'text-neutral-500'}`} data-testid="photo-count">
                {selectedPhotos.length} / {MAX_PHOTOS}
              </span>
            </div>
            <button
              type="button"
              onClick={onSelectPhotos}
              disabled={isSubmitting || selectedPhotos.length >= MAX_PHOTOS}
              className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 text-neutral-300 hover:border-white flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="select-photos-btn"
            >
              <FolderOpen className="w-4 h-4" />
              {selectedPhotos.length === 0 ? 'Seleccionar desde Google Drive' : 'Agregar / Cambiar fotos'}
            </button>
            {selectedPhotos.length > 0 && (
              <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="selected-photos-grid">
                {selectedPhotos.map((photo, index) => {
                  const isPersisted = !!photo.file_key;
                  const previewSrc = isPersisted
                    ? photo.thumbnail_url || photo.url
                    : photo.thumbnailLink || photo.url;
                  return (
                    <div key={photo.file_key || photo.id || index} className="relative aspect-square group bg-neutral-800" data-testid={`selected-photo-${index}`}>
                      <img
                        src={previewSrc}
                        alt={photo.name || ''}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(photo, index)}
                        disabled={isSubmitting}
                        className="absolute top-1 right-1 p-1.5 bg-black/80 text-white hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-30"
                        title="Eliminar foto"
                        data-testid={`remove-photo-${index}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      {isPersisted && (
                        <span className="absolute bottom-1 left-1 px-1.5 py-0.5 bg-green-600/90 text-white text-[10px]">
                          Guardada
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-neutral-700 text-neutral-300 hover:text-white hover:border-white disabled:opacity-50"
              data-testid="cancel-form-btn"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-white text-black font-medium hover:bg-neutral-200 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="create-project-submit"
            >
              {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSubmitting ? 'Procesando...' : project ? 'Actualizar' : 'Crear Proyecto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ProjectForm;
